"use client";

import React, { useState } from "react";
import Image from "next/image";
import type { ITestimonial } from "@/types/testimonial";
import BlobImage from "../Home/BlobImage";

type CardTone = "navy" | "dark";

interface TestimonialCardProps {
  testimonial: ITestimonial;
  /** Alternating background used down the list (navy → charcoal → navy…). */
  tone?: CardTone;
}

const TONE_STYLES: Record<CardTone, string> = {
  navy: "bg-[#1B365E]",
  dark: "bg-[#333333]",
};

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  testimonial,
  tone = "navy",
}) => {
  const [expanded, setExpanded] = useState(false);
  const { name, role, image, rating, date, review } = testimonial;

  return (
    <article
      className={`${TONE_STYLES[tone]} text-white rounded-3xl p-6 sm:p-8 lg:p-10 relative overflow-hidden z-2`}
    >
      <div className="relative z-10 flex md:flex-row lg:justify-between justify-center flex-col items-start gap-8">
        {/* Left - Portrait (desktop only) */}
        {image && (
          <BlobImage src={image} alt={`Photo de ${name}`} />
        )}

        {/* Right - Content */}
        <div className="flex-1 text-center md:text-left max-w-171.5">
          {/* Header: author + rating */}
          <header className="w-full flex flex-col-reverse md:flex-row md:items-start justify-center md:justify-between gap-4 mb-4 md:mb-10">
            <div className="max-w-107.5 mx-auto md:mx-0">
              <h3 className="text-[16px] leading-6.5 font-bold tracking-[0.32px]">
                {name}, <span className="font-normal">{role}</span>
              </h3>
            </div>
            <div className="flex items-center gap-2 justify-center md:py-0 py-1">
              <span className="text-white text-[15px] leading-5 tracking-[0.2px] font-bold mr-0.5">
                {rating}
              </span>
              <svg
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M8.38586 0.691212C8.68521 -0.230099 9.98862 -0.2301 10.288 0.691211L11.8066 5.36497C11.9404 5.777 12.3244 6.05596 12.7576 6.05596H17.6719C18.6406 6.05596 19.0434 7.29557 18.2597 7.86497L14.284 10.7535C13.9335 11.0082 13.7868 11.4595 13.9207 11.8716L15.4393 16.5453C15.7386 17.4666 14.6842 18.2327 13.9004 17.6633L9.9247 14.7748C9.57421 14.5202 9.09962 14.5202 8.74913 14.7748L4.77339 17.6633C3.98968 18.2327 2.9352 17.4666 3.23455 16.5453L4.75314 11.8716C4.88702 11.4595 4.74036 11.0082 4.38987 10.7535L0.414135 7.86497C-0.369579 7.29557 0.0331967 6.05596 1.00192 6.05596H5.9162C6.34943 6.05596 6.73339 5.777 6.86726 5.36497L8.38586 0.691212Z"
                  fill="#FFBC42"
                />
              </svg>
              <span className="text-[#40BFDE] text-[12px] leading-5 font-normal tracking-[0.2px]">
                {date}
              </span>
            </div>
          </header>

          {/* Review body */}
          <div className="space-y-4 sm:space-y-5">
            <p
              className={`text-white text-[12px] sm:text-[14px] leading-5 font-normal tracking-[0.3px] ${
                expanded ? "" : "line-clamp-5"
              }`}
            >
              {review}
            </p>
          </div>

          {/* Expand / collapse */}
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            className="mt-7 sm:mt-8 flex items-center justify-center w-full md:justify-start cursor-pointer gap-2 text-[#FC7F16] hover:opacity-80 transition-all duration-300 group"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className={`transition-transform duration-300 ${expanded ? "rotate-90" : ""}`}
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z"
                fill="currentColor"
              />
            </svg>
            <span className="text-[15px] leading-4 font-bold tracking-[0.2px]">
              {expanded ? "Afficher moins" : "Afficher plus"}
            </span>
          </button>
        </div>
      </div>
      {/* Background */}
      <div className="absolute left-32 sm:left-0 inset-0 top-0 w-full h-full">
        <Image
          src="/assets/testimonials.svg"
          alt="Background decoration"
          title="Background decoration"
          fill
          className="object-cover 3xl:object-contain"
        />
      </div>
    </article>
  );
};

export default TestimonialCard;
